package com.tovtalk.presentation.viewmodel.home

import com.tovtalk.data.local.TokenManager
import com.tovtalk.domain.entity.Post
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.entity.FeedItem
import com.tovtalk.domain.entity.buildFeedItems
import com.tovtalk.domain.entity.FeedAdKind
import com.tovtalk.domain.entity.toggleLike
import com.tovtalk.domain.repository.AdsRepository
import com.tovtalk.domain.repository.AppRepository
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.data.local.DiscoverCache
import com.tovtalk.domain.usecase.post.GetDiscoverPostsUseCase
import com.tovtalk.domain.usecase.post.IncreaseViewCountUseCase
import com.tovtalk.domain.usecase.post.LikePostUseCase
import com.tovtalk.presentation.state.PagingState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flowOn
import kotlinx.coroutines.launch

import com.tovtalk.presentation.ViewModel

/**
 * Home ViewModel - פיד ראשי (Reels Only Architecture)
 * 
 * כולל:
 * - טעינת רילסים עם pagination
 * - ספירת צפיות (view tracking)
 * - Like/Unlike
 * - Follow/Unfollow מהפיד
 */
class HomeViewModel(
    private val getDiscoverPostsUseCase: GetDiscoverPostsUseCase,
    private val likePostUseCase: LikePostUseCase,
    private val increaseViewCountUseCase: IncreaseViewCountUseCase,
    private val postRepository: com.tovtalk.domain.repository.PostRepository,
    private val settingsRepository: com.tovtalk.domain.repository.SettingsRepository,
    private val userRepository: UserRepository,
    private val adsRepository: AdsRepository,
    private val appRepository: AppRepository,
    private val scope: CoroutineScope
) : ViewModel() {
    private val _reelsState = MutableStateFlow(PagingState<Post>())
    val postsState: StateFlow<PagingState<Post>> = _reelsState.asStateFlow()
    
    private val _currentUserId = MutableStateFlow<Int?>(null)
    val currentUserId: StateFlow<Int?> = _currentUserId.asStateFlow()
    
    private val _currentUserIsReadOnly = MutableStateFlow(false)
    val currentUserIsReadOnly: StateFlow<Boolean> = _currentUserIsReadOnly.asStateFlow()

    /** True after getCurrentUser() has completed (Success or Error). Used to avoid showing upload tab until we know if user is read-only. */
    private val _currentUserLoaded = MutableStateFlow(false)
    val currentUserLoaded: StateFlow<Boolean> = _currentUserLoaded.asStateFlow()

    private val _audioAutoplay = MutableStateFlow(true)
    val audioAutoplay: StateFlow<Boolean> = _audioAutoplay.asStateFlow()

    /** Saved pager index so returning to Home restores scroll position. */
    private val _savedReelPage = MutableStateFlow(0)
    val savedReelPage: StateFlow<Int> = _savedReelPage.asStateFlow()

    /** Increments whenever the user explicitly requests a feed refresh (e.g. re-tapping Home). */
    private val _refreshTick = MutableStateFlow(0)
    val refreshTick: StateFlow<Int> = _refreshTick.asStateFlow()

    /** Kashrut restriction dialog (e.g. follow blocked) */
    private val _showKashrutDialog = MutableStateFlow(false)
    val showKashrutDialog: StateFlow<Boolean> = _showKashrutDialog.asStateFlow()
    private val _kashrutDialogMessage = MutableStateFlow<String?>(null)
    val kashrutDialogMessage: StateFlow<String?> = _kashrutDialogMessage.asStateFlow()

    /** Save/unsave error message – show to user and clear after display */
    private val _saveError = MutableStateFlow<String?>(null)
    val saveError: StateFlow<String?> = _saveError.asStateFlow()

    private val _showAds = MutableStateFlow(false)
    private val _adsEveryNPosts = MutableStateFlow(5)

    /** Admin-controlled flag: whether guests see the reaction (like) icon on reels. */
    private val _guestReactionsIconVisible = MutableStateFlow(false)
    val guestReactionsIconVisible: StateFlow<Boolean> = _guestReactionsIconVisible.asStateFlow()

    private val _feedItemsState = MutableStateFlow<List<FeedItem>>(emptyList())
    val feedItemsState: StateFlow<List<FeedItem>> = _feedItemsState.asStateFlow()

    /** Set once when the VM is initialised from a deep link. Never changes after first load. */
    private var pinnedPostRef: String? = null

    fun setSavedReelPage(page: Int) {
        _savedReelPage.value = page.coerceAtLeast(0)
    }

    /**
     * Re-tapping Home while already on Home: drop cached feed, reset scroll, load fresh.
     * The refreshTick signals UI (pager / scroll container) to reset position so the
     * user lands on a new first video instead of the one they were watching.
     */
    fun refreshFeed() {
        DiscoverCache.clear()
        pinnedPostRef = null
        _savedReelPage.value = 0
        // Clear items so the previously-playing video doesn't linger during the network fetch.
        // Don't set isLoading=true here — loadReels() bails out if already loading.
        _reelsState.value = PagingState(items = emptyList(), cursor = null, hasMore = true, isLoading = false)
        _refreshTick.value = _refreshTick.value + 1
        loadReels(refresh = true)
    }

    fun dismissKashrutDialog() {
        _showKashrutDialog.value = false
        _kashrutDialogMessage.value = null
    }

    fun clearSaveError() {
        _saveError.value = null
    }

    /**
     * Called by HomeScreen when opened from a deep link.
     * Fetches the pinned post in parallel with the first feed page,
     * then prepends it. No-op if already initialised.
     */
    fun setPinnedPost(ref: String) {
        if (pinnedPostRef != null) return   // already set, don't re-pin on recompositions
        pinnedPostRef = ref
        // reload from scratch so the pinned post lands at index 0
        scope.launch {
            loadReelsWithPin(ref)
        }
    }

    private suspend fun loadReelsWithPin(ref: String) {
        _reelsState.value = _reelsState.value.copy(isLoading = true)
        coroutineScope {
            val pinnedDeferred = async {
                when (val r = postRepository.getPost(ref)) {
                    is DomainResult.Success -> r.data
                    else -> null
                }
            }
            val feedDeferred = async {
                getDiscoverPostsUseCase(cursor = null, limit = 15)
            }
            val pinnedPost = pinnedDeferred.await()
            val feedResult = feedDeferred.await()

            when (feedResult) {
                is DomainResult.Success -> {
                    val feedPage = feedResult.data
                    val feedItems = feedPage.posts.filter { it.ref != ref }
                    val items = if (pinnedPost != null) listOf(pinnedPost) + feedItems else feedItems
                    _reelsState.value = PagingState(
                        items = items,
                        cursor = feedPage.nextCursor,
                        isLoading = false,
                        hasMore = feedPage.hasMore
                    )
                    DiscoverCache.setFirstPage(items)
                    // Store in PostDetailCache so internal navigation is fast
                    if (pinnedPost != null) {
                        com.tovtalk.data.local.PostDetailCache.set(pinnedPost)
                    }
                }
                else -> {
                    // Feed failed — try to show just the pinned post at minimum
                    val pinned = pinnedPost
                    if (pinned != null) {
                        _reelsState.value = PagingState(
                            items = listOf(pinned),
                            isLoading = false,
                            hasMore = false
                        )
                    } else {
                        _reelsState.value = _reelsState.value.copy(isLoading = false)
                    }
                }
            }
        }
    }

    fun toggleAudioAutoplay() {
        scope.launch {
            settingsRepository.setAudioAutoplay(!_audioAutoplay.value)
        }
    }

    init {
        loadCurrentUser()
        scope.launch {
            _audioAutoplay.value = settingsRepository.getAudioAutoplay()
        }
        scope.launch {
            settingsRepository.observeAudioAutoplay().collect { _audioAutoplay.value = it }
        }
        // Fetch ads frequency + flags from server config
        scope.launch {
            val config = appRepository.getAppConfig()
            if (config is DomainResult.Success) {
                _adsEveryNPosts.value = config.data.adsEveryNPosts
                _guestReactionsIconVisible.value = config.data.guestReactionsIconVisible
            }
        }
        // Combine posts + showAds + frequency to produce feedItems.
        // Feed ads are VIDEO ads (sponsored reels); cadence is server-driven.
        scope.launch {
            combine(_reelsState, _showAds, _adsEveryNPosts) { reelState, showAds, everyN ->
                buildFeedItems(reelState.items, showAds, everyN, adKind = FeedAdKind.VIDEO)
            }.flowOn(Dispatchers.Default).collect { items ->
                _feedItemsState.value = items
            }
        }
        // Determine showAds from user
        scope.launch {
            userRepository.getCachedUser().collect { user ->
                val isGuest = TokenManager.isGuestMode
                _showAds.value = adsRepository.shouldShowFeedAds(
                    showAdsFlag = user?.showFeedAds,
                    isGuest = isGuest
                )
            }
        }
        // For guests, getCachedUser() may never emit — set immediately
        if (TokenManager.isGuestMode) {
            _showAds.value = true
        }
        // HomeViewModel is a DI singleton, so without an explicit reset the previous account's
        // read-only flag, currentUserId and the (global) DiscoverCache feed bleed into the next
        // session. Observe auth changes (logout / login / guest→registered) and wipe/reload
        // per-user state. Skip the first (current) emission so we don't duplicate init's work.
        scope.launch {
            var first = true
            TokenManager.authStateFlow.collect { state ->
                if (first) { first = false; return@collect }
                DiscoverCache.clear()
                _currentUserId.value = null
                _currentUserIsReadOnly.value = false
                _currentUserLoaded.value = false
                _reelsState.value = PagingState(items = emptyList(), cursor = null, hasMore = true, isLoading = false)
                if (state.isLoggedIn) {
                    loadCurrentUser()
                    _showAds.value = TokenManager.isGuestMode
                } else {
                    _showAds.value = false
                }
            }
        }
        // Don't load reels here — load only when feed tab (0) is visible (see HomeFeedContent).
        // Otherwise switching to Messages/Notifications etc. would recreate the VM and trigger discover again.
    }
    
    private fun loadCurrentUser() {
        scope.launch {
            if (TokenManager.isGuestMode) return@launch
            when (val result = userRepository.getCurrentUser()) {
                is DomainResult.Success -> {
                    _currentUserId.value = result.data.id
                    _currentUserIsReadOnly.value = result.data.isReadOnly
                }
                else -> {}
            }
            _currentUserLoaded.value = true
        }
    }
    
    fun loadPosts(refresh: Boolean = false, onFirstPageLoaded: ((List<Post>) -> Unit)? = null) = loadReels(refresh, onFirstPageLoaded)
    
    fun loadReels(refresh: Boolean = false, onFirstPageLoaded: ((List<Post>) -> Unit)? = null) {
        if (_reelsState.value.isLoading) return
        
        scope.launch {
            val cursor = if (refresh) null else _reelsState.value.cursor
            val isFirstPage = refresh && cursor == null

            // Show cached data immediately while loading from network
            val hadCached = if (isFirstPage) {
                val cached = DiscoverCache.getFirstPage()
                if (!cached.isNullOrEmpty()) {
                    _reelsState.value = PagingState(
                        items = cached,
                        isLoading = true,
                        hasMore = true
                    )
                    true
                } else {
                    // No full page cached — try hero post as placeholder
                    val hero = DiscoverCache.getHeroPost()
                    if (hero != null) {
                        _reelsState.value = PagingState(
                            items = listOf(hero),
                            isLoading = true,
                            hasMore = true
                        )
                    } else {
                        _reelsState.value = _reelsState.value.copy(isLoading = true)
                    }
                    false
                }
            } else {
                _reelsState.value = _reelsState.value.copy(isLoading = true)
                false
            }

            when (val result = getDiscoverPostsUseCase(cursor = cursor, limit = 15)) {
                is DomainResult.Success -> {
                    if (isFirstPage && hadCached) {
                        // Keep current (cached) items so the playing video never jumps; add only new ones from network
                        val current = _reelsState.value.items
                        val currentIds = current.mapTo(mutableSetOf(), Post::id)
                        val fromNetwork = result.data.posts
                        val toAppend = fromNetwork.filter { it.id !in currentIds }
                        if (toAppend.isEmpty()) {
                            // Nothing new — don't update state at all, so no recomposition and no jump
                            _reelsState.value = _reelsState.value.copy(isLoading = false)
                            return@launch
                        }
                        val merged = current + toAppend
                        if (merged.isNotEmpty()) {
                            onFirstPageLoaded?.invoke(merged)
                            DiscoverCache.setFirstPage(merged)
                        }
                        _reelsState.value = PagingState(
                            items = merged,
                            cursor = result.data.nextCursor,
                            isLoading = false,
                            hasMore = result.data.hasMore
                        )
                    } else {
                        val feedPage = result.data
                        val newItems = if (refresh) {
                            feedPage.posts
                        } else {
                            _reelsState.value.items + feedPage.posts
                        }
                        if (isFirstPage && newItems.isNotEmpty()) {
                            onFirstPageLoaded?.invoke(newItems)
                            DiscoverCache.setFirstPage(newItems)
                        }
                        _reelsState.value = PagingState(
                            items = newItems,
                            cursor = feedPage.nextCursor,
                            isLoading = false,
                            hasMore = feedPage.hasMore
                        )
                    }
                }
                is DomainResult.Error -> {
                    _reelsState.value = _reelsState.value.copy(
                        isLoading = false,
                        hasMore = false,
                        error = result.message
                    )
                }
                is DomainResult.Loading -> {
                    _reelsState.value = _reelsState.value.copy(isLoading = true)
                }
            }
        }
    }
    
    fun loadMore() {
        if (_reelsState.value.isLoadingMore || !_reelsState.value.hasMore || _reelsState.value.isLoading) return
        
        scope.launch {
            _reelsState.value = _reelsState.value.copy(isLoadingMore = true)
            
            val cursor = _reelsState.value.cursor

            when (val result = getDiscoverPostsUseCase(cursor = cursor, limit = 15)) {
                is DomainResult.Success -> {
                    val feedPage = result.data
                    val pinRef = pinnedPostRef
                    val newPosts = if (pinRef != null) feedPage.posts.filter { it.ref != pinRef } else feedPage.posts
                    _reelsState.value = PagingState(
                        items = _reelsState.value.items + newPosts,
                        cursor = feedPage.nextCursor,
                        isLoadingMore = false,
                        hasMore = feedPage.hasMore
                    )
                }
                is DomainResult.Error -> {
                    _reelsState.value = _reelsState.value.copy(
                        isLoadingMore = false,
                        error = result.message
                    )
                }
                is DomainResult.Loading -> {
                    _reelsState.value = _reelsState.value.copy(isLoadingMore = true)
                }
            }
        }
    }

    fun onPostVisible(feedIndex: Int) {
        val currentItems = _reelsState.value.items
        if (currentItems.isEmpty()) return

        // feedIndex is the pager index which includes ads.
        // Count only VideoItems up to feedIndex to get the real post position.
        val feedItems = _feedItemsState.value
        val videoIndex = feedItems.take(feedIndex + 1).count { it is FeedItem.VideoItem } - 1

        if (videoIndex >= currentItems.size - 2) {
            loadMore()
        }
    }
    
    fun likePost(postId: Int, isLiked: Boolean) {
        scope.launch {
            when (likePostUseCase(postId, isLiked)) {
                is DomainResult.Success -> {
                    // Update local state via shared helper
                    _reelsState.value = _reelsState.value.copy(
                        items = _reelsState.value.items.toggleLike(postId, isLiked)
                    )
                }
                is DomainResult.Error -> {
                    // Handle error
                }
                is DomainResult.Loading -> {}
            }
        }
    }

    /**
     * שליחת ספירת צפייה לשרת
     * נקרא מ-VideoViewTracker כשעוברים את ה-threshold
     */
    fun increaseViewCount(postId: Int) {
        scope.launch {
            when (val result = increaseViewCountUseCase(postId)) {
                is DomainResult.Success -> {
                    // עדכון local state
                    val updatedItems = _reelsState.value.items.map { post ->
                        if (post.id == postId) {
                            post.copy(stats = post.stats.copy(views = post.stats.views + 1))
                        } else {
                            post
                        }
                    }
                    _reelsState.value = _reelsState.value.copy(items = updatedItems)
                }
                is DomainResult.Error -> {
                    // Log error but don't show to user
                }
                is DomainResult.Loading -> {}
            }
        }
    }
    
    /**
     * קבלת Post לפי אינדקס
     */
    fun getPostAt(index: Int): Post? {
        return _reelsState.value.items.getOrNull(index)
    }
    
    /**
     * קבלת כל ה-Posts
     */
    fun getAllPosts(): List<Post> {
        return _reelsState.value.items
    }
    
    /**
     * Increment comment count for a post (after adding a comment)
     */
    fun incrementCommentCount(postId: Int) {
        val updatedItems = _reelsState.value.items.map { post ->
            if (post.id == postId) {
                post.copy(stats = post.stats.copy(comments = post.stats.comments + 1))
            } else {
                post
            }
        }
        _reelsState.value = _reelsState.value.copy(items = updatedItems)
    }
    
    /**
     * Save/Unsave reel. Optimistic update: UI toggles immediately; on API error we revert and show message.
     */
    fun savePost(postId: Int, isSaved: Boolean) {
        scope.launch {
            val previousItems = _reelsState.value.items
            // Optimistic update – user sees immediate feedback
            val optimisticItems = previousItems.map { post ->
                if (post.id == postId) {
                    post.copy(
                        viewerState = post.viewerState.copy(isSaved = !isSaved),
                        stats = post.stats.copy(saves = if (isSaved) post.stats.saves - 1 else post.stats.saves + 1)
                    )
                } else post
                }
            _reelsState.value = _reelsState.value.copy(items = optimisticItems)

            val result = if (isSaved) postRepository.unsavePost(postId) else postRepository.savePost(postId)
            when (result) {
                is DomainResult.Success -> { /* already updated optimistically */ }
                is DomainResult.Error -> {
                    // Revert optimistic update
                    _reelsState.value = _reelsState.value.copy(items = previousItems)
                    _saveError.value = result.message
                }
                is DomainResult.Loading -> {}
            }
        }
    }

    /**
     * Follow user directly from feed
     */
    fun followUser(userId: Int) {
        scope.launch {
            when (val result = userRepository.followUser(userId)) {
                is DomainResult.Success -> {
                    // Update local state - mark user as followed in all their posts
                    val updatedItems = _reelsState.value.items.map { post ->
                        if (post.author.id == userId) {
                            post.copy(
                                viewerState = post.viewerState.copy(isFollowing = true)
                            )
                        } else {
                            post
                        }
                    }
                    _reelsState.value = _reelsState.value.copy(items = updatedItems)
                }
                is DomainResult.Error -> {
                    if (result.isKashrutNotAllowed) {
                        _kashrutDialogMessage.value = result.message
                        _showKashrutDialog.value = true
                    }
                }
                is DomainResult.Loading -> {}
            }
        }
    }
}
